import React from "react";
import Modal from "../../../Ui/Modal";
import ButtonComponent from "../../../Ui/Button";
import CircularLoader from "../../../Ui/Loader";
import { humanizeEnum } from "../../../utils/utils";

const warrantyStatusStyles = {
  SUBMITTED: "bg-[#FFF7E8] text-[#B86E00] border border-[#FFD699]",
  APPROVED: "bg-[#F2FBF4] text-[#2F8F48] border border-[#B9E7C7]",
  DENIED: "bg-[#FFF1F1] text-[#C62828] border border-[#F4BBBB]",
  RESOLVED: "bg-[#F3F6FB] text-[#49627A] border border-[#D9E3EF]",
};

const formatIssue = (value) => {
  if (!value || typeof value !== "string") return "N/A";
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const formatEnumValue = (value) => {
  if (!value || typeof value !== "string") return "N/A";
  const trimmed = value.trim();
  if (!trimmed) return "N/A";
  const looksLikeEnum = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/.test(trimmed);
  return looksLikeEnum ? humanizeEnum(trimmed) : trimmed;
};

const formatAddress = (address = {}) =>
  [
    address?.streetAddress,
    address?.unitApt ? `Apt ${address.unitApt}` : null,
    [address?.city, address?.state, address?.zipCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ") || "N/A";

const getAttachmentUrl = (item) => item?.fileUrl || item?.file_url || "";
const getAttachmentName = (item) => item?.originalName || item?.original_name || "Open attachment";

const isImageAttachment = (item) => {
  const source = `${getAttachmentUrl(item)} ${getAttachmentName(item)}`.toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(source);
};

const DetailRow = ({ label, value, multiline = false, valueClassName = "" }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-[#242424] text-[14px] font-medium min-w-[140px]">
      {label}
    </span>
    <span
      className={`text-[#595959] text-[14px] break-words text-left ${
        multiline ? "whitespace-pre-wrap" : ""
      } ${valueClassName}`}
    >
      {value || "N/A"}
    </span>
  </div>
);

const MediaList = ({ title, items }) => {
  if (!items?.length) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-[16px] font-semibold text-[#242424]">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((item) => (
          <a
            key={item.id}
            href={getAttachmentUrl(item)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-md border border-[#ECECEC] p-3 text-[14px] text-[#242424] hover:border-[#3CAB5C]"
          >
            {isImageAttachment(item) ? (
              <img
                src={getAttachmentUrl(item)}
                alt={getAttachmentName(item)}
                className="h-14 w-14 rounded-md object-cover border border-[#ECECEC] bg-white shrink-0"
              />
            ) : (
              <div className="h-14 w-14 rounded-md border border-[#ECECEC] bg-[#F8F8F8] text-[#595959] text-[12px] font-medium flex items-center justify-center shrink-0">
                File
              </div>
            )}
            <span className="text-[#595959] text-[14px] break-all">
              {getAttachmentName(item)}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};

const WarrantyClaimReviewModal = ({
  open,
  onClose,
  claim,
  loading = false,
  reviewNotes = "",
  onReviewNotesChange,
  allowReview = true,
  extraFooterActions = null,
  onApprove,
  onDeny,
  decisionLoading = false,
}) => {
  const statusKey = String(claim?.status || "").toUpperCase();
  const canReview = allowReview && statusKey === "SUBMITTED";
  const maintenanceRequest = claim?.maintenanceRequestDetails || {};
  const propertyAddress = maintenanceRequest?.propertyAddress || {};

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="756px"
      title="Warranty Details"
      footer={
        <div className="flex w-full justify-between">
          <ButtonComponent paddingY="10px" onClick={onClose}>
            Close
          </ButtonComponent>
          <div className="flex gap-2">
            {extraFooterActions}
            {canReview && (
              <ButtonComponent
                bgColor="#D40000"
                color="#FFFFFF"
                borderColor="#D40000"
                paddingY="10px"
                onClick={onDeny}
                disabled={decisionLoading}
              >
                {decisionLoading ? "Saving..." : "Deny Claim"}
              </ButtonComponent>
            )}
            {canReview && (
              <ButtonComponent
                bgColor="#3CAB5C"
                color="#FFFFFF"
                paddingY="10px"
                onClick={onApprove}
                disabled={decisionLoading}
              >
                {decisionLoading ? "Saving..." : "Approve Claim"}
              </ButtonComponent>
            )}
          </div>
        </div>
      }
    >
      {loading ? (
        <CircularLoader minHeight="280px" />
      ) : claim ? (
        <div className="px-4 py-4 space-y-5">
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-[16px] font-semibold text-[#242424]">
                Warranty Claim Details
              </h3>
              <span
                className={`px-3 py-1 rounded-full text-[13px] font-medium ${
                  warrantyStatusStyles[statusKey] ||
                  "bg-[#F6F6F6] text-[#595959] border border-[#ECECEC]"
                }`}
              >
                {claim?.statusDisplay || formatEnumValue(statusKey) || "Unknown"}
              </span>
            </div>
            <div className="space-y-3">
              <DetailRow
                label="Warranty #"
                value={claim?.warrantyNumber || claim?.warranty_number || claim?.id}
              />
              <DetailRow
                label="Job #"
                value={claim?.jobNumber || maintenanceRequest?.jobNumber}
              />
              <DetailRow
                label="Submitted By"
                value={claim?.submittedBy?.fullName || claim?.submittedBy?.email}
              />
              <DetailRow
                label="Submitted At"
                value={formatDateTime(claim?.createdAt)}
              />
              <DetailRow
                label="Service Category"
                value={formatIssue(claim?.serviceCategory || claim?.service_category)}
              />
              <DetailRow label="Property" value={claim?.property} />
              <DetailRow
                label="Reported Issue"
                value={claim?.details || "No additional details were provided."}
                multiline
              />
            </div>
          </div>

          <MediaList title="Claim Attachments" items={claim?.media || []} />

          <div className="space-y-3">
            <h3 className="text-[16px] font-semibold text-[#242424]">
              Original Job Details
            </h3>
            <div className="space-y-3">
              <DetailRow label="Job #" value={maintenanceRequest?.jobNumber} />
              <DetailRow
                label="Original Status"
                value={maintenanceRequest?.statusDisplay || formatEnumValue(maintenanceRequest?.status)}
              />
              <DetailRow
                label="Completed At"
                value={formatDateTime(maintenanceRequest?.completedAt)}
              />
              <DetailRow
                label="Assigned Vendor"
                value={
                  maintenanceRequest?.assignedVendor?.fullName ||
                  maintenanceRequest?.assignedVendor?.email
                }
              />
              <DetailRow
                label="Address"
                value={formatAddress(propertyAddress)}
                multiline
              />
              <DetailRow
                label="Original Description"
                value={
                  maintenanceRequest?.description ||
                  "No original description was provided."
                }
                multiline
              />
            </div>
          </div>

          <MediaList
            title="Original Job Attachments"
            items={maintenanceRequest?.media || []}
          />

          <div className="space-y-2">
            <h3 className="text-[16px] font-semibold text-[#242424]">
              Vendor Notes
            </h3>
            <textarea
              value={reviewNotes}
              onChange={(event) => onReviewNotesChange?.(event.target.value)}
              placeholder="Add review notes. A reason is required if you deny the claim."
              className="w-full min-h-[120px] border border-[#ECECEC] rounded-md p-2 text-[14px] text-[#242424] outline-none focus:ring-2 focus:ring-[#3CAB5C]"
              disabled={!canReview || decisionLoading}
            />
          </div>
        </div>
      ) : (
        <div className="px-6 py-10 text-center text-[#595959]">
          Warranty details could not be loaded.
        </div>
      )}
    </Modal>
  );
};

export default WarrantyClaimReviewModal;
